/// <amd-module name="Tfs/Frame/Components/Profile/MeControl" />
import * as React from "react";

import { IMeControlData } from "Tfs/Frame/Components/Profile/Contracts/DataModels";
import { IMeControlService } from "Tfs/Frame/Components/Profile/Service/MeControlService";
import { MeControlConstants } from "Tfs/Frame/Components/Profile/Util/MeControlConstants";
import * as Resources from "Tfs/Frame/Resources";
import { IVssContentService } from "VSS/Platform/Content";
import { IVssContributionEventService, IVssContributionService } from "VSS/Platform/Contribution";
import { IdentityImageSize, getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { ClientTheme, IVssThemeService } from "VSS/Platform/Theming";

const dataProviderId = "ms.vss-tfs-web.vertical-header-me-control-data-provider";

export interface IMeControlComponentState {
    data: IMeControlData;
    configDetails: object;
}

export class MeControl extends VssComponent<{}, IMeControlComponentState> {
    private themeService: IVssThemeService;

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        const pageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const meControlData = contributionService.getData<IMeControlData>(dataProviderId)!;

        let imageUrl = "";

        if (pageData && pageData.user) {
            imageUrl = getIdentityImageUrl(context.pageContext, pageData.user, IdentityImageSize.large) + `&` + new Date().getTime();
        }

        this.themeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService");

        const meControlService = this.context.pageContext.getService<IMeControlService>("IMeControlService");
        this.state = {
            data: meControlData,
            configDetails: meControlService.getConfigurations(meControlData, imageUrl)
        };
    }

    public render(): JSX.Element {
        return (
            <div
                className={
                    "me-control-div commandbar-item commandbar-icon flex-row flex-noshrink " +
                    (this.state.data && this.state.data.currentAccount.isAnonymous ? "sign-in" : "")
                }
                role="menuitem"
            >
                <div id="me-control-container">
                    <span>{Resources.LoadingProfileMenuItems}</span>
                </div>
            </div>
        );
    }

    public componentDidMount() {
        super.componentDidMount();
        if (this.state.data) {
            this._loadMeControlScriptAfterTTI();
        }
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        const contributedEventService = this.context.pageContext.getService<IVssContributionEventService>("IVssContributionEventService");
        contributedEventService.unsubscribe(this._onLoadMeControlScript, "page-rendered");
        this.themeService.unsubscribe(this._onSelectedThemeChanged, "themeChanged");

        // Unmount the Preact component that was dynmically created by the MeControl script
        try {
            const meControlContainer = document.getElementById("me-control-container");
            if (meControlContainer) {
                const meControlRoot = meControlContainer.getElementsByClassName("mectrl_root");
                if (meControlRoot?.length == 1) {
                    const component = (meControlRoot[0] as any)._component;
                    if (component) {
                        this.unmountComponent(component);
                    }
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    // This is a copy of the unmountComponent function from preact, which is what the external MeControl component uses
    // We don't have refernces to preact modules so the best option here was to copy the logic over.
    unmountComponent(component: any) {
        let base = component.base;
        component._disable = true;
        if (component.componentWillUnmount) {
            component.componentWillUnmount();
        }

        component.base = null;

        // recursively tear down & recollect high-order component children:
        let inner = component._component;
        if (inner) {
            this.unmountComponent(inner);
        } else if (base) {
            component.nextBase = base;
            this.removeNode(base);
            this.removeChildren(base);
        }

        this.applyRef(component.__ref, null);
    }

    removeNode(node: Element) {
        let parentNode = node.parentNode;
        if (parentNode) {
            parentNode.removeChild(node);
        }
    }

    removeChildren(node: any) {
        node = node.lastChild;
        while (node) {
            let next = node.previousSibling;
            this.recollectNodeTree(node);
            node = next;
        }
    }
    recollectNodeTree(node: any) {
        let component = node._component;
        if (component) {
            // if node is owned by a Component, unmount that component (ends up recursing back here)
            this.unmountComponent(component);
        } else {
            // If the node's VNode had a ref function, invoke it with null here.
            // (this is part of the React spec, and smart for unsetting references)
            this.removeChildren(node);
        }
    }

    applyRef(ref: any, value: any) {
        if (ref) {
            if (typeof ref == "function") {
                ref(value);
            } else {
                ref.current = value;
            }
        }
    }

    private _onSelectedThemeChanged = (clientTheme: ClientTheme): void => {
        const meControl = (window as any).MeControl;
        if (meControl && meControl.API) {
            meControl.API.setTheme({ type: clientTheme.isDark ? "dark" : "light" });
        }
    };

    private _loadMeControlScriptAfterTTI() {
        const loaded = (window as any).MeControl;
        if (!loaded) {
            const contributedEventService = this.context.pageContext.getService<IVssContributionEventService>("IVssContributionEventService");
            contributedEventService.subscribe(this._onLoadMeControlScript, "page-rendered");
        } else {
            const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            this._loadMeControlComponent(telemetryService);
        }
    }

    private _onLoadMeControlScript = () => {
        const contentService = this.context.pageContext.getService<IVssContentService>("IVssContentService");
        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

        if (this.state.data.meControlScriptUrl) {
            const contentAttribute = {
                url: this.state.data.meControlScriptUrl,
                contentLength: 16000,
                clientId: "me-control-script",
                contributionId: "vertical-header-profile",
                contentType: "text/javascript"
            };
            performanceService.startScenario(MeControlConstants.LoadScriptScenario, false);

            contentService.loadScripts([contentAttribute]).then(
                (result) => {
                    performanceService.endScenario(MeControlConstants.Area, MeControlConstants.LoadScriptScenario, false);
                    this._loadMeControlComponent(telemetryService);

                    if ($DEBUG) {
                        console.log("successfully loaded me control script");
                    }
                },
                (reason) => {
                    performanceService.endScenario(MeControlConstants.Area, MeControlConstants.LoadScriptScenario, false, undefined, {
                        status: "failed"
                    });

                    telemetryService.publishEvent(MeControlConstants.Area, MeControlConstants.LoadScriptFeature, {
                        Action: MeControlConstants.LoadScriptFailAction,
                        ErrorMessage: JSON.stringify(reason)
                    });
                    if ($DEBUG) {
                        console.log("failed to load me control script");
                    }
                }
            );
        }
    };

    private _loadMeControlComponent = (telemetryService: IVssTelemetryService) => {
        try {
            const loadSuccess = (window as any).MeControl.Loader.load(this.state.configDetails);

            if (!loadSuccess) {
                telemetryService.publishEvent(MeControlConstants.Area, MeControlConstants.LoadComponentFeature, {
                    Action: MeControlConstants.LoadComponentFailAction
                });
                if ($DEBUG) {
                    console.log("Me Control did not load successfully");
                }
            } else {
                this.themeService.subscribe(this._onSelectedThemeChanged, "themeChanged");
            }
        } catch (e) {
            console.error(e);
        }
    };
}

VssComponent.register("ms.vss-tfs-web.me-control", MeControl);
